import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, ArrowLeft, CheckCircle, Sparkles, AlertCircle } from 'lucide-react';

import { API_BASE_URL as API_BASE } from '@/services/api';

export function ForgotPassword() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setErrorMessage(t('forgotPassword.emailRequired', 'Please enter your email address'));
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || t('forgotPassword.sendFailed', 'Failed to send reset link'));
      }

      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : t('common.somethingWentWrong', 'Something went wrong'));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-4">
        <Card className="border border-gray-200 shadow-sm bg-white">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-14 h-14 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center mb-3 shadow-lg">
              <Mail className="h-7 w-7 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">
              {t('forgotPassword.title', 'Reset your password')}
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              {t('forgotPassword.subtitle', "Enter your email and we'll send you a reset link")}
            </p>
          </CardHeader>

          <CardContent className="space-y-5 pt-2">
            {status === 'success' ? (
              <div className="text-center space-y-4 py-4">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center animate-bounce">
                  <CheckCircle className="h-9 w-9 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('forgotPassword.checkEmail', 'Check your email!')}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {t('forgotPassword.checkEmailDesc', "We've sent a password reset link to your email address. Check your inbox and spam folder.")}
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/login-traditional')}
                  variant="outline"
                  className="w-full h-11 font-semibold"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {t('forgotPassword.backToLogin', 'Back to login')}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {status === 'error' && errorMessage && (
                  <Alert className="border-red-200 bg-red-50">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-700 text-sm">
                      {errorMessage}
                    </AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                    {t('forgotPassword.emailLabel', 'Email address')}
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('loginPage.emailPlaceholder', 'your.email@example.com')}
                      className="pl-10 h-12 border-2 border-gray-200 focus:border-primary-400 rounded-xl text-base"
                      autoFocus
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white font-semibold text-base shadow-lg hover:shadow-xl transition-all transform hover:scale-[1.02] active:scale-[0.98]"
                  disabled={status === 'loading'}
                >
                  {status === 'loading' ? (
                    <span className="flex items-center">
                      <Sparkles className="mr-2 h-5 w-5 animate-spin" />
                      {t('forgotPassword.sending', 'Sending...')}
                    </span>
                  ) : (
                    <span className="flex items-center">
                      {t('forgotPassword.sendReset', 'Send reset link')}
                      <Mail className="ml-2 h-5 w-5" />
                    </span>
                  )}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => navigate('/login-traditional')}
                    className="text-sm text-gray-500 hover:text-primary-600 font-medium hover:underline transition-colors inline-flex items-center"
                  >
                    <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
                    {t('forgotPassword.backToLogin', 'Back to login')}
                  </button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default ForgotPassword;
